"use client";

import type { ReactNode } from "react";
import { type LucideIcon } from "@/components/icons";
import { Card } from "@/components/ui/card";
import { ErrorReportActions } from "@/components/error/error-report-actions";

export interface ErrorPageBodyProps {
  error: Error & { digest?: string };
  reset: () => void;
  title: string;
  description?: string;
  icon: LucideIcon;
  navButton: ReactNode;
  showStack: boolean;
  reportTitlePrefix?: string;
  reportable?: boolean;
}

function ErrorPageBody({
  error,
  reset,
  title,
  description,
  icon: Icon,
  navButton,
  showStack,
  reportTitlePrefix,
  reportable = true,
}: ErrorPageBodyProps) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="mb-1 flex size-12 items-center justify-center rounded-full bg-muted">
          <Icon aria-hidden="true" className="size-6 text-muted-foreground" />
        </div>
        <h1 className="text-balance text-xl font-semibold">{title}</h1>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      <ErrorReportActions
        error={error}
        reset={reset}
        navButton={navButton}
        reportTitlePrefix={reportTitlePrefix}
        reportable={reportable}
      />
      <ErrorDevDetails error={error} showStack={showStack} />
    </div>
  );
}

export function ErrorPageCard({
  variant,
  ...props
}: ErrorPageBodyProps & { variant: "centered" | "card" }) {
  if (variant === "centered") {
    return (
      <div className="w-full max-w-sm">
        <ErrorPageBody {...props} />
      </div>
    );
  }
  return (
    <Card padding="roomy" className="w-full max-w-sm">
      <ErrorPageBody {...props} />
    </Card>
  );
}

function ErrorDevDetails({
  error,
  showStack,
}: {
  error: Error;
  showStack: boolean;
}) {
  if (process.env.NEXT_PUBLIC_ENVIRONMENT === "production") return null;

  return (
    <details className="w-full text-left">
      <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">
        Error details (development only)
      </summary>
      <pre className="mt-2 overflow-auto whitespace-pre-wrap rounded-sm border bg-muted p-3 text-xs">
        {error.message}
        {showStack && error.stack && `\n\n${error.stack}`}
      </pre>
    </details>
  );
}
